<script setup lang="ts">
type Props = {
  modelValue: boolean
  top?: number | string
  width?: number | string
  overlay?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  top: 61,
  width: 100,
})

const emit = defineEmits(['update:modelValue'])

const route = useRoute()

const componentClasses = computed(() => {
  const classes = ['app-navigation-drawer']

  if (props.modelValue) classes.push('app-navigation-drawer--active')

  return classes
})

const componentStyles = computed(() => {
  return {
    top: `${props.top}px`,
    width: `${props.width}%`,
    height: `calc(100% - ${props.top}px)`,
  }
})

const overlayStyles = computed(() => {
  return {
    top: `${props.top}px`,
    height: `calc(100% - ${props.top}px)`,
  }
})

const onClose = () => emit('update:modelValue', false)

const setScrollState = (state: boolean) => {
  state ? scrollLock() : scrollUnlock()
}

watch(
  () => props.modelValue,
  (state) => setScrollState(state),
)

watch(
  () => route?.name,
  () => onClose(),
)

const touchService = new TouchService({
  distance: 100,
  direction: 'left',
  onTouchEnd: onClose,
})
</script>

<template>
  <transition v-if="overlay" name="ui-fade">
    <div
      v-if="modelValue"
      class="app-navigation-drawer-overlay"
      data-test-id="navigation-drawer-overlay"
      :style="overlayStyles"
      @click="onClose"
    />
  </transition>

  <nav
    v-bind="$attrs"
    role="menubar"
    :class="componentClasses"
    :style="componentStyles"
    data-test-id="navigation-drawer"
    @touchstart="touchService.onStart"
    @touchmove="touchService.onMove"
    @touchend="touchService.onEnd"
  >
    <div class="app-navigation-drawer__content">
      <slot />
    </div>
  </nav>
</template>

<style lang="scss" scoped>
.app-navigation-drawer-overlay {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  width: 100%;
  height: 100%;
  background-color: rgba(var(--ui-black-rgb), 0.2);
}

.app-navigation-drawer {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 1000;
  height: 100%;
  background-color: var(--ui-white);
  box-shadow: 0 8px 10px -5px rgba(var(--ui-black-rgb), 0.2);
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  transform: translateX(-110%);

  &--active {
    transform: translateX(0);
  }

  &__content {
    max-width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
  }
}
</style>
